import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { categories, articles } from '@/mocks/articles';

const categoryMeta: Record<string, { title: string; description: string; keywords: string }> = {
  'shishang-chuanda': {
    title: '熟女时尚穿搭 - 成熟女性穿搭风格指南 | 熟女魅力',
    description: '探索适合30岁以上成熟女性的时尚穿搭风格。从职场着装到休闲搭配，提供实用的熟女穿搭技巧、配色方案和单品推荐，展现独特的熟女时尚品味与自信气质。',
    keywords: '熟女穿搭,熟女时尚,成熟女性穿搭,熟女风格指南,职场女性穿搭,熟女搭配技巧',
  },
  'hufu-baoyang': {
    title: '熟女护肤保养 - 成熟女性抗衰老护肤方案 | 熟女魅力',
    description: '针对30岁以上女性的专业护肤保养指南。涵盖抗衰老方案、日常护理、美容饮食与养生技巧，帮助熟女由内而外焕发健康光彩，展现成熟女性的独特魅力。',
    keywords: '熟女护肤,熟女保养,抗衰老护肤,成熟女性护肤,熟女美容,女性保养指南',
  },
  'shenghuo-meixue': {
    title: '熟女生活美学 - 成熟女性的品质生活方式 | 熟女魅力',
    description: '分享熟女的生活智慧与美学理念。从家居布置到品质生活，从茶艺花道到旅行见闻，打造属于成熟女性的优雅生活方式，让每一天都充满美好与仪式感。',
    keywords: '熟女生活,熟女美学,品质生活,家居布置,女性生活方式,熟女居家',
  },
  'ziwo-chengzhang': {
    title: '熟女自我成长 - 成熟女性的人生进阶之路 | 熟女魅力',
    description: '关注熟女的内心世界与自我提升。从情绪管理到职业发展，从终身学习到心理健康，助力每位成熟女性活出更精彩、更丰盈的人生下半场。',
    keywords: '熟女成长,女性自我提升,成熟女性人生,熟女心理,女性学习,女性发展',
  },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const meta = categoryMeta[slug || ''] || categoryMeta['shishang-chuanda'];
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-800 mb-3">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-label text-sm cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: category.name,
    description: category.description,
    url: `https://elegantvibe.github.io/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '熟女魅力',
      url: 'https://elegantvibe.github.io/',
    },
  };

  return (
    <>
      <SEOHead
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalUrl={`https://elegantvibe.github.io/category/${slug}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema, null, 2) }}
      />
      <Navbar />
      <main className="min-h-screen pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: category.name },
            ]}
          />

          <div className="relative rounded-2xl overflow-hidden h-48 md:h-64 mb-12">
            <img
              src={category.image}
              alt={category.name}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <h1 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                {category.name}
              </h1>
              <p className="text-sm text-background-200/90 font-label max-w-xl">
                {category.description}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-label transition-all cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50 font-medium'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-16">
            {categoryArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="relative w-full h-48 overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 text-xs text-foreground-400 font-label mb-3">
                    <span>{article.author}</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span>{article.date}</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span>{article.readTime}</span>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mb-2 line-clamp-2" style={{ fontFamily: 'var(--font-heading)' }}>
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-500 font-label leading-relaxed line-clamp-2">
                    {article.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}